import axiosInstance from "core/api/axiosInstance";

const paymentExcelResponseError = async (data) => {
  let body = data;
  if (data instanceof Blob) {
    const text =
      typeof data.text === "function"
        ? await data.text()
        : await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(data);
          });
    body = text;
  }
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch (_) {
      return null;
    }
  }
  const message = body?.detail || body?.message;
  if (typeof message !== "string" || !message.trim()) return null;
  const error = new Error(message.replace(/\s*\(download:\s*true\)/gi, "").trim());
  if (/descargados previamente/i.test(message)) error.severity = "warning";
  return error;
};

export const getPaymentExcel = async (keyConcil, signal) => {
  const key = String(keyConcil || "").trim();
  if (!key) throw new Error("Selecciona una llave de conciliación.");
  validateAuth();
  let response;
  try {
    response = await axiosInstance.get("/api/py/dispersion/exportar-excel-financiera", {
      params: { key_concil: key },
      headers: createHeaders({
        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      responseType: "blob",
      timeout: 60000,
      signal,
    });
  } catch (error) {
    if (signal?.aborted || !error.response) throw error;
    throw (await paymentExcelResponseError(error.response.data)) || error;
  }
  const file = response.data;
  if (
    /json/i.test(file?.type || response.headers?.["content-type"] || "") ||
    !(file instanceof Blob)
  )
    throw (
      (await paymentExcelResponseError(file)) ||
      new Error("El servidor no devolvió un Excel de pago.")
    );
  if (!(file instanceof Blob) || !file.size)
    throw new Error("El servidor no devolvió un Excel de pago.");
  if (/json|text\/html/i.test(file.type))
    throw new Error("El servidor devolvió un error en lugar del Excel de pago.");
  return { file, filename: `Excel_de_pago_${key.replace(/[^\w-]/g, "_")}.xlsx` };
};

export const sendConciliationFinal = async ({ key_concil, code, value_final, finops_values }) => {
  if (!key_concil || !code || !Number.isSafeInteger(value_final) || value_final <= 0)
    throw new Error("La llave, el código y un valor a pagar válido son obligatorios.");
  if (finops_values !== undefined) {
    if (
      !Array.isArray(finops_values) ||
      !finops_values.length ||
      finops_values.some(
        (item) =>
          typeof item?.name !== "string" ||
          !item.name.trim() ||
          !Number.isSafeInteger(item.value) ||
          item.value <= 0
      )
    )
      throw new Error("Completa el nombre y un valor a pagar válido de cada peaje.");
    const names = finops_values.map((item) => item.name.trim().toUpperCase());
    const total = finops_values.reduce((sum, item) => sum + item.value, 0);
    if (
      new Set(names).size !== names.length ||
      !Number.isSafeInteger(total) ||
      total !== value_final
    )
      throw new Error("El desglose de peajes debe ser único y sumar el valor final de pago.");
  }
  validateAuth();
  const response = await axiosInstance.post(
    "/api/conciliation/send-conciliation-fnlc",
    {
      key_concil: String(key_concil),
      code: String(code),
      value_final,
      ...(finops_values !== undefined
        ? { finops_values: finops_values.map(({ name, value }) => ({ name: name.trim(), value })) }
        : {}),
    },
    { headers: createHeaders(), timeout: CONFIG.TIMEOUTS.DEFAULT }
  );
  if (!response.data || (response.data.code !== undefined && response.data.code !== 0))
    throw new Error(response.data?.message || "No se pudo enviar el valor a pagar.");
  return response.data;
};

const CONFIG = {
  ALLOWED_FILE_TYPES: [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp",
    "image/svg+xml",
    "image/tiff",
    "image/x-icon",
  ],
  ALLOWED_EXTENSIONS: [
    "pdf",
    "jpeg",
    "jpg",
    "png",
    "gif",
    "bmp",
    "webp",
    "svg",
    "tiff",
    "tif",
    "ico",
  ],
  MAX_FILE_SIZE: 50 * 1024 * 1024,
  TIMEOUTS: {
    DEFAULT: 15000,
    UPLOAD: 300000,
    FILE_FETCH: 30000,
    PREVIEW: 60000,
    HEAVY_QUERY: 240000,
  },
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
};

const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    return {
      hasToken: !!token,
      token: token || null,
      tokenLength: token ? token.length : 0,
      hasUser: !!user,
      userData: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    console.error("Error obteniendo información de autenticación:", error);
    return {
      hasToken: false,
      token: null,
      tokenLength: 0,
      hasUser: false,
      userData: null,
    };
  }
};

const validateAuth = () => {
  const authInfo = getAuthInfo();
  if (!authInfo.hasToken) {
    throw new Error("No hay token de autenticación. Por favor inicie sesión.");
  }
  return authInfo;
};

const createHeaders = (additionalHeaders = {}) => {
  const authInfo = getAuthInfo();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${authInfo.token}`,
    ...additionalHeaders,
  };
};

const handleServiceError = (error, serviceName) => {
  console.error(`Error en ${serviceName}:`, error);

  if (
    error.code === "ERR_CANCELED" ||
    error.name === "CanceledError" ||
    error.name === "AbortError"
  ) {
    return {
      error: true,
      message: "Petición cancelada",
      details: { canceled: true },
      shouldLogout: false,
      isCanceled: true,
      isTimeout: false,
    };
  }

  let errorMessage = `Error en ${serviceName}`;
  let errorDetails = {};

  if (error.response) {
    const backendMessage = error.response.data?.message || error.response.data?.msm || "";
    const backendError = error.response.data?.error || "";

    errorMessage =
      backendMessage ||
      backendError ||
      `Error ${error.response.status}: ${error.response.statusText || "Error desconocido"}`;

    errorDetails = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers,
    };

    if (error.response.status === 401) {
      errorMessage =
        backendMessage || "No autorizado. Su sesión ha expirado o el token es inválido.";
    } else if (error.response.status === 403) {
      errorMessage =
        backendMessage || "Acceso denegado. No tiene permisos para realizar esta acción.";
    } else if (error.response.status === 404) {
      errorMessage =
        backendMessage || backendError || "El servidor no encontró el recurso solicitado (404).";
    } else if (error.response.status >= 500) {
      errorMessage = backendMessage || "Error interno del servidor.";
    }
  } else if (error.request) {
    errorMessage =
      "La solicitud está tomando más tiempo de lo esperado. El procesamiento podría continuar en segundo plano.";
    errorDetails = { request: error.request, isTimeout: true };
  } else {
    errorMessage = error.message || "Error de configuración de la solicitud";
    errorDetails = { message: error.message, stack: error.stack };
  }

  return {
    error: true,
    message: errorMessage,
    details: errorDetails,
    shouldLogout: false,
    isCanceled: false,
    isTimeout: error.code === "ECONNABORTED" || error.message?.includes("timeout"),
  };
};

const createResponse = (success, data, message, extra = {}) => ({
  success,
  data: data || [],
  count: Array.isArray(data) ? data.length : 0,
  message: message || (success ? "Operación exitosa" : "Error en la operación"),
  timestamp: new Date().toISOString(),
  ...extra,
});

const withRetry = async (fn, retries = CONFIG.MAX_RETRIES, delay = CONFIG.RETRY_DELAY) => {
  try {
    return await fn();
  } catch (error) {
    if (
      error.code === "ERR_CANCELED" ||
      error.name === "CanceledError" ||
      error.name === "AbortError"
    ) {
      throw error;
    }

    const status = error.response?.status;

    if (
      retries === 0 ||
      status === 401 ||
      status === 403 ||
      status === 404 ||
      (status >= 400 && status < 500)
    ) {
      throw error;
    }

    console.log(`Reintentando petición... Quedan ${retries} intentos`);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return withRetry(fn, retries - 1, delay);
  }
};

// ================================================
// KEYS DE CONCILIACIÓN
// 🆕 Preserva `id` y `file`
// ================================================
export const getConciliationKeys = async () => {
  try {
    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.DEFAULT,
    };

    const response = await withRetry(() =>
      axiosInstance.get("/api/conciliation/get-conciliation-keys", config)
    );

    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    if (status < 200 || status >= 300) {
      throw new Error(data.msm || data.message || "Error al obtener las llaves de conciliación");
    }

    const keys = data.data || [];

    const normalizedKeys = keys
      .map((item) => {
        if (typeof item === "string") {
          const v = item.trim();
          if (!v) return null;
          return { value: v, label: v, id: null, file: null };
        }

        const keyValue = item.key_concil ?? item.llave ?? item.llave_concil ?? null;

        if (keyValue === null || keyValue === undefined || keyValue === "") {
          return null;
        }

        const strValue = String(keyValue).trim();
        if (!strValue) return null;

        return {
          value: strValue,
          label: strValue,
          id: item.id ?? null,
          file: item.file ?? null,
        };
      })
      .filter(Boolean);

    // Dedup prefiriendo la entrada que tenga `file`
    const mapByValue = new Map();
    normalizedKeys.forEach((k) => {
      const prev = mapByValue.get(k.value);
      if (!prev) {
        mapByValue.set(k.value, k);
      } else if (!prev.file && k.file) {
        mapByValue.set(k.value, k);
      }
    });

    const dedupedKeys = Array.from(mapByValue.values());

    return createResponse(
      true,
      dedupedKeys,
      data.message || "Llaves de conciliación obtenidas exitosamente",
      {
        total: data.count || dedupedKeys.length,
        responseStatus: status,
      }
    );
  } catch (error) {
    console.error("Error en getConciliationKeys:", error);
    const errorResult = handleServiceError(error, "getConciliationKeys");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

// ================================================
// CONCILIACIONES (LISTADO PAGINADO)
// ================================================
export const getConciliations = async (params = {}, signal = null) => {
  try {
    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.FILE_FETCH,
      ...(signal ? { signal } : {}),
    };

    const normalizedParams = { ...params };
    if (normalizedParams.key_concil !== undefined && normalizedParams.key_concil !== "") {
      normalizedParams.key_concil = String(normalizedParams.key_concil).trim();
    }

    const queryParams = new URLSearchParams();
    Object.keys(normalizedParams).forEach((key) => {
      if (
        normalizedParams[key] !== null &&
        normalizedParams[key] !== undefined &&
        normalizedParams[key] !== ""
      ) {
        queryParams.append(key, normalizedParams[key]);
      }
    });

    const url = `/api/conciliation/get-conciliations${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await withRetry(() => axiosInstance.get(url, config));

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    let data = response.data.data || [];

    data = data.filter(
      (item) => item.key_concil !== null && item.key_concil !== undefined && item.key_concil !== ""
    );

    data.sort((a, b) => {
      const statusA = String(a.status || "0");
      const statusB = String(b.status || "0");

      const aIsTwo = statusA === "2";
      const bIsTwo = statusB === "2";

      if (aIsTwo && !bIsTwo) return -1;
      if (!aIsTwo && bIsTwo) return 1;

      return parseInt(b.id) - parseInt(a.id);
    });

    const dataEnriquecida = data.map((item) => ({
      ...item,
      has_files: false,
      files_data: [],
      files_count: 0,
    }));

    const statusTwoCount = dataEnriquecida.filter((item) => String(item.status) === "2").length;

    return createResponse(
      true,
      dataEnriquecida,
      response.data.message || "Conciliaciones obtenidas exitosamente",
      {
        code: response.data.code,
        total: response.data.count || dataEnriquecida.length,
        statusTwoCount,
        receivedCount: response.data.data?.length || 0,
      }
    );
  } catch (error) {
    console.error("Error en getConciliations:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getConciliations");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const getConciliationDetail = async (id, params = {}) => {
  try {
    if (!id) {
      throw new Error("El ID de la conciliación es requerido");
    }

    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.FILE_FETCH,
      params,
    };

    const response = await withRetry(() =>
      axiosInstance.get(`/api/conciliation/get-conciliations-detail/${id}`, config)
    );

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    return createResponse(
      true,
      response.data.data || [],
      response.data.message || "Detalle de conciliación obtenido exitosamente",
      { code: response.data.code }
    );
  } catch (error) {
    console.error(`Error en getConciliationDetail para ID ${id}:`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getConciliationDetail");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const getConciliationFilesById = async (idConcil, code, signal = null) => {
  try {
    if (!idConcil) {
      throw new Error("El ID de la conciliación es requerido");
    }
    if (code === null || code === undefined || String(code).trim() === "") {
      throw new Error("El código (concesión) es requerido");
    }

    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.FILE_FETCH,
      ...(signal ? { signal } : {}),
    };

    const idParam = encodeURIComponent(String(idConcil).trim());
    const codeParam = encodeURIComponent(String(code).trim());
    const url = `/api/conciliation/get-conciliations-files/${idParam}/${codeParam}`;

    const response = await withRetry(() => axiosInstance.get(url, config));

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    const files = response.data.data || [];

    const filesWithDetails = files.map((file) => ({
      id: file.id,
      key_concil: file.key_concil,
      concil_id: file.concil_id ?? idConcil,
      code_op: file.code_op ?? String(code),
      filename: file.file?.originalname || file.file?.filename || "Sin nombre",
      filepath: file.file?.path || file.file?.destination || "",
      mimetype: file.file?.mimetype || "application/octet-stream",
      size: file.file?.size || 0,
      createdAt: file.createdAt || file.created_at,
      updatedAt: file.updatedAt || file.updated_at,
      user_create: file.user_create ?? null,
      file: file.file,
    }));

    return createResponse(
      true,
      filesWithDetails,
      response.data.message || "Archivos obtenidos exitosamente",
      {
        code: response.data.code,
        total: response.data.count || files.length,
        concilId: idConcil,
        codeOp: String(code),
      }
    );
  } catch (error) {
    console.error(`Error en getConciliationFilesById (${idConcil}/${code}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getConciliationFilesById");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const getConciliationFilePreview = async (fileId) => {
  try {
    if (!fileId) {
      throw new Error("El ID del archivo es requerido");
    }

    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.PREVIEW,
      responseType: "blob",
    };

    const response = await withRetry(() =>
      axiosInstance.get(`/api/conciliation/get-conciliations-file-preview/${fileId}`, config)
    );

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    const blob = response.data;
    const arrayBuffer = await blob.slice(0, 100).arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    const isPDF = bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
    const isPNG = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
    const isJPEG = bytes[0] === 0xff && bytes[1] === 0xd8;
    const isGIF = bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38;
    const isBMP = bytes[0] === 0x42 && bytes[1] === 0x4d;
    const isWEBP = bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46;

    let contentType = response.headers["content-type"] || "application/octet-stream";

    if (isPDF) contentType = "application/pdf";
    else if (isPNG) contentType = "image/png";
    else if (isJPEG) contentType = "image/jpeg";
    else if (isGIF) contentType = "image/gif";
    else if (isBMP) contentType = "image/bmp";
    else if (isWEBP) contentType = "image/webp";

    const blobWithCorrectType = new Blob([blob], { type: contentType });
    const blobUrl = URL.createObjectURL(blobWithCorrectType);

    return {
      success: true,
      data: {
        blob: blobWithCorrectType,
        url: blobUrl,
        contentType,
        size: blob.size || 0,
        isImage: isPNG || isJPEG || isGIF || isBMP || isWEBP,
        isPDF,
      },
      message: "Preview del archivo obtenido exitosamente",
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error(`Error en getConciliationFilePreview para ID ${fileId}:`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getConciliationFilePreview");
    return {
      success: false,
      data: null,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
      timestamp: new Date().toISOString(),
    };
  }
};

export const getConciliationDetailByKey = async (key, params = {}, signal = null) => {
  try {
    if (!key) {
      throw new Error("La llave de conciliación es requerida");
    }

    validateAuth();

    const queryParams = new URLSearchParams();
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v !== null && v !== undefined && v !== "") {
        queryParams.append(k, v);
      }
    });

    const url = `/api/conciliation/get-conciliations-keys-data/${key}${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.HEAVY_QUERY,
      ...(signal ? { signal } : {}),
    };

    const response = await axiosInstance.get(url, config);

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    const payload = response.data.data || {};

    const definitive = Array.isArray(payload);
    const conciliations = definitive ? payload : payload.conciliations || payload.data || [];
    if (!Array.isArray(conciliations))
      throw new Error("El resumen por llave tiene un formato inválido");

    let conciliationId =
      payload.id?.id ??
      (typeof payload.id === "string" || typeof payload.id === "number" ? payload.id : null);

    let totalRegistros = Number(
      payload.conciliationsCount?.total_registros ??
        payload.total_registros?.total_registros ??
        payload.total_registros?.total_registro ??
        0
    );

    let valorTotal = Number(
      payload.concilTotal?.total_conciliacion ?? payload.valor_total?.total_conciliacion ?? 0
    );

    const totalAbonoNov = Number(payload.concilCountAbonoNov?.totalAbonoNov ?? 0);
    const totalAjusteNov = Number(payload.concilCountAjusteNov?.totalAjusteNov ?? 0);

    const normalized = conciliations.map((c) => {
      if (!definitive)
        return {
          concesion: c.concesion,
          ...(Object.prototype.hasOwnProperty.call(c, "status") ? { status: c.status } : {}),
          key_concil: c.key_concil,
          realName: c.realName,
          name: c.realName,
          ...(c.tipo_pago != null ? { tipo_pago: c.tipo_pago } : {}),
          countconcil: Number(c.countConcil ?? c.countconcil ?? 0),
          conciliation_id: conciliationId,
        };
      if (
        !c ||
        c.code_operator == null ||
        !c.key_concil ||
        !(
          String(c.key_concil) === String(key) ||
          (/^PRE_\d+$/.test(String(key)) && String(c.key_concil) === String(key).slice(4))
        )
      )
        throw new Error("El resumen definitivo no corresponde a la llave solicitada");
      let more = c.more;
      if (typeof more === "string") {
        try {
          more = JSON.parse(more);
        } catch (_) {
          more = null;
        }
      }
      const name = more?.concesion || c.concesion || c.realName || "";
      return {
        ...c,
        concesion: String(c.code_operator),
        key_concil: String(key),
        realName: name,
        name,
        countconcil: Number(c.steps ?? 0),
        conciliation_id: c.conciliation_id ?? c.concil_id ?? c.id ?? null,
        detailReference: String(key),
        estado_cuadre: c.estado_cuadre ?? more?.totales_dia?.estado_cuadre ?? null,
        created_at: c.created_at ?? c.createdAt,
        conciliation_date: c.conciliation_date ?? more?.fecha,
        value_gp: c.value_gp ?? more?.totales_dia?.value_gp ?? more?.totales_dia?.total_gopass ?? 0,
      };
    });
    if (definitive) {
      const ids = [
        ...new Set(normalized.map((row) => row.conciliation_id).filter((id) => id != null)),
      ];
      conciliationId = ids.length === 1 ? ids[0] : null;
      totalRegistros = normalized.reduce((sum, row) => sum + row.countconcil, 0);
      valorTotal = normalized.reduce((sum, row) => sum + Number(row.value_gp), 0);
    }

    return createResponse(true, normalized, response.data.message || "Detalle por llave obtenido", {
      code: response.data.code,
      total: normalized.length,
      totalRegistros,
      valorTotal,
      totalAbonoNov,
      totalAjusteNov,
      conciliationId,
      raw: payload,
    });
  } catch (error) {
    if (
      error.code === "ERR_CANCELED" ||
      error.name === "CanceledError" ||
      error.name === "AbortError"
    ) {
      return createResponse(false, [], "Petición cancelada", {
        isCanceled: true,
        shouldLogout: false,
      });
    }

    console.error(`Error en getConciliationDetailByKey para llave ${key}:`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getConciliationDetailByKey");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: false,
    });
  }
};

export const getConciliationDetailByIdAndConcession = async (
  id,
  concesion,
  params = {},
  signal = null
) => {
  try {
    if (!id) {
      throw new Error("El ID de la conciliación es requerido");
    }
    if (!concesion) {
      throw new Error("La concesión es requerida");
    }

    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.FILE_FETCH,
      ...(signal ? { signal } : {}),
      params,
    };

    const response = await withRetry(() =>
      axiosInstance.get(`/api/conciliation/get-conciliations-detail/${id}/${concesion}`, config)
    );

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    const payload = response.data.data || {};
    const rows = Array.isArray(payload) ? payload : payload.data || [];

    const totalRegistros = Number(
      payload.total_registros?.total_registro ?? payload.total_registros?.total_registros ?? 0
    );
    const valorTotal = Number(payload.valor_total?.total_conciliacion ?? 0);

    return createResponse(true, rows, response.data.message || "Detalle obtenido exitosamente", {
      code: response.data.code,
      total: rows.length,
      totalRegistros,
      valorTotal,
      raw: payload,
    });
  } catch (error) {
    console.error(`Error en getConciliationDetailByIdAndConcession (${id}/${concesion}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getConciliationDetailByIdAndConcession");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const getNovelties = async (keyConcil, concesion, signal = null) => {
  try {
    if (!keyConcil) {
      throw new Error("La llave de conciliación es requerida");
    }
    if (concesion === null || concesion === undefined || String(concesion).trim() === "") {
      throw new Error("La concesión es requerida");
    }

    validateAuth();

    const keyParam = encodeURIComponent(String(keyConcil).trim());
    const concesionParam = encodeURIComponent(String(concesion).trim());
    const url = `/api/conciliation/get-novelties/${keyParam}/${concesionParam}`;

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.FILE_FETCH,
      ...(signal ? { signal } : {}),
    };

    const response = await withRetry(() => axiosInstance.get(url, config));

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    const raw = response.data.data;
    const novelties = Array.isArray(raw) ? raw : raw?.novelties ?? raw?.novedades ?? raw?.data;
    if (!Array.isArray(novelties)) {
      throw new Error(
        "El servicio de novedades devolvió un formato no reconocido. No se puede confirmar que la consulta esté vacía."
      );
    }
    if (!novelties.length && Number(response.data.count) > 0) {
      throw new Error(
        "El servicio reporta novedades, pero no entregó los registros. Vuelve a consultar."
      );
    }

    const normalized = novelties.map((item) => ({
      id: item.id ?? null,
      code: item.code ?? item.concesion ?? concesion,
      key_concil: item.key_concil ?? keyConcil,
      type_nov: item.type_nov ?? null,
      value: Number(item.value ?? 0),
      observation: item.observation ?? "",
      peaje: item.peaje ?? null,
      createdAt: item.createdAt || item.created_at || null,
      updatedAt: item.updatedAt || item.updated_at || null,
      user_create: item.user_create ?? null,
      raw: item,
    }));

    const totalValue = normalized.reduce((acc, n) => acc + (Number(n.value) || 0), 0);

    return createResponse(
      true,
      normalized,
      response.data.message || "Novedades obtenidas exitosamente",
      {
        code: response.data.code,
        total: response.data.count || normalized.length,
        totalValue,
        keyConcil: String(keyConcil),
        concesion: String(concesion),
      }
    );
  } catch (error) {
    console.error(`Error en getNovelties (${keyConcil}/${concesion}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getNovelties");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      totalValue: 0,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const getPeajes = async (code) => {
  try {
    if (!code && code !== 0) {
      throw new Error("El código (concesión) es requerido");
    }

    validateAuth();

    const codeParam = encodeURIComponent(String(code).trim());
    const url = `/api/conciliation/get-peajes/${codeParam}`;

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.FILE_FETCH,
    };

    const response = await withRetry(() => axiosInstance.get(url, config));

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    const raw = response.data.data;
    const peajes = Array.isArray(raw) ? raw : raw?.peajes || raw?.data || [];

    const normalized = peajes
      .map((p) => ({
        peaje: p.peaje ?? p.nombre ?? "",
        hora_inicio: p.hora_inicio ?? null,
        hora_fin: p.hora_fin ?? null,
        pago_independiente: p.pago_independiente ?? null,
      }))
      .filter((p) => p.peaje);

    return createResponse(
      true,
      normalized,
      response.data.message || "Peajes obtenidos exitosamente",
      { code: response.data.code, total: normalized.length }
    );
  } catch (error) {
    console.error(`Error en getPeajes para code ${code}:`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getPeajes");
    return createResponse(false, [], errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const addNovelty = async ({
  code,
  key_concil,
  type_nov,
  value,
  observation,
  peaje = null,
}) => {
  try {
    if (!code && code !== 0) {
      throw new Error("El código (concesión) es requerido");
    }
    if (!key_concil) {
      throw new Error("La llave de conciliación es requerida");
    }
    if (type_nov === null || type_nov === undefined || type_nov === "") {
      throw new Error("El tipo de novedad es requerido");
    }
    if (value === null || value === undefined || value === "") {
      throw new Error("El valor es requerido");
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      throw new Error("El valor debe ser numérico");
    }

    const obs = String(observation || "").trim();
    if (obs.length < 10) {
      throw new Error("La observación debe tener mínimo 10 caracteres");
    }

    validateAuth();

    const payload = {
      code: Number(code),
      key_concil: String(key_concil).trim(),
      type_nov: Number(type_nov),
      value: numericValue,
      observation: obs,
      peaje: peaje ? String(peaje).trim() : null,
    };

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.DEFAULT,
    };

    // Un POST puede haberse guardado aunque se pierda la respuesta.
    const response = await axiosInstance.post("/api/conciliation/add-novelties", payload, config);

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    return createResponse(
      true,
      response.data.data || payload,
      response.data.message || "Novedad creada exitosamente",
      { code: response.data.code, payload }
    );
  } catch (error) {
    console.error("Error en addNovelty:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "addNovelty");
    return createResponse(false, null, errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const uploadConciliationFiles = async (
  files,
  keyConcil,
  idConcil,
  code,
  onProgress = null
) => {
  try {
    validateAuth();

    if (!keyConcil) {
      throw new Error("La llave de conciliación es requerida.");
    }
    if (!idConcil) {
      throw new Error("El ID de la conciliación es requerido.");
    }
    if (!code) {
      throw new Error("El código (concesión) es requerido.");
    }
    if (!files || files.length === 0) {
      throw new Error("Debe seleccionar al menos un archivo para subir.");
    }

    for (const file of files) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isValidType =
        CONFIG.ALLOWED_FILE_TYPES.includes(file.type) ||
        CONFIG.ALLOWED_EXTENSIONS.includes(fileExtension);

      if (!isValidType) {
        throw new Error(
          `Formato de archivo no válido: ${file.name}. Solo se permiten PDF, JPEG, PNG, GIF, BMP, WEBP, SVG y TIFF.`
        );
      }

      if (file.size > CONFIG.MAX_FILE_SIZE) {
        throw new Error(
          `El archivo ${file.name} es demasiado grande. Máximo ${
            CONFIG.MAX_FILE_SIZE / 1024 / 1024
          }MB permitidos.`
        );
      }
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("key_concil", String(keyConcil));
    formData.append("id_concil", String(idConcil));
    formData.append("code", String(code));

    const config = {
      headers: {
        ...createHeaders(),
        "Content-Type": "multipart/form-data",
      },
      timeout: CONFIG.TIMEOUTS.UPLOAD,
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const total = progressEvent.total || progressEvent.event?.total || 0;
          const loaded = progressEvent.loaded || progressEvent.event?.loaded || 0;
          const percentCompleted = total > 0 ? Math.round((loaded * 100) / total) : 0;
          onProgress(percentCompleted);
        }
      },
    };

    const response = await withRetry(() =>
      axiosInstance.post("/api/conciliation/upload_files", formData, config)
    );

    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    if (status < 200 || status >= 300) {
      throw new Error(data.msm || data.message || "Error al subir los archivos");
    }

    return createResponse(
      true,
      data.data || null,
      data.msm || data.message || "Archivos subidos exitosamente",
      {
        filesCount: files.length,
        keyConcil,
        idConcil,
        code,
        responseStatus: status,
      }
    );
  } catch (error) {
    console.error("Error en uploadConciliationFiles:", error);
    const errorResult = handleServiceError(error, "uploadConciliationFiles");

    if (errorResult.isTimeout) {
      return createResponse(false, null, errorResult.message, {
        error: errorResult.message,
        isTimeout: true,
        details: errorResult.details,
        isCanceled: errorResult.isCanceled,
      });
    }

    return createResponse(false, null, errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const uploadExcelFinanciera = async (file, keyConcil, concilId, onProgress = null) => {
  try {
    validateAuth();

    if (!file) throw new Error("El archivo es requerido");
    if (!keyConcil) throw new Error("La llave de conciliación es requerida");
    if (!concilId) throw new Error("El ID de la conciliación es requerido");

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(fileExtension)) {
      throw new Error("Solo se permiten archivos Excel (.xlsx o .xls)");
    }

    const MAX_SIZE = 40 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      throw new Error(`El archivo supera el máximo de ${MAX_SIZE / 1024 / 1024}MB`);
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("key_concil", String(keyConcil));
    formData.append("concil_id", String(concilId));

    const config = {
      headers: {
        ...createHeaders(),
        "Content-Type": "multipart/form-data",
      },
      timeout: CONFIG.TIMEOUTS.UPLOAD,
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const total = progressEvent.total || 0;
          const loaded = progressEvent.loaded || 0;
          const percent = total > 0 ? Math.round((loaded * 100) / total) : 0;
          onProgress(percent);
        }
      },
    };

    const response = await withRetry(() =>
      axiosInstance.post("/api/conciliation/import-concil-fncl", formData, config)
    );

    if (!response.data) throw new Error("No se recibió respuesta del servidor");

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    return createResponse(
      true,
      response.data.data || null,
      response.data.message || "Excel financiera subido exitosamente",
      { keyConcil, concilId }
    );
  } catch (error) {
    console.error("Error en uploadExcelFinanciera:", error);
    const errorResult = handleServiceError(error, "uploadExcelFinanciera");
    return createResponse(false, null, errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export const getExcelFinanciera = async (keyConcil, concilId) => {
  try {
    if (!keyConcil) throw new Error("La llave es requerida");
    if (!concilId) throw new Error("El ID de la conciliación es requerido");

    validateAuth();

    const keyParam = encodeURIComponent(String(keyConcil).trim());
    const concilParam = encodeURIComponent(String(concilId).trim());
    const url = `/api/conciliation/get-concil-fncl/${keyParam}/${concilParam}`;

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.FILE_FETCH,
    };

    const response = await withRetry(() => axiosInstance.get(url, config));

    if (!response.data) throw new Error("No se recibió respuesta del servidor");

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    return createResponse(true, response.data.data || null, response.data.message || "OK", {
      keyConcil,
      concilId,
    });
  } catch (error) {
    console.error("Error en getExcelFinanciera:", error);
    const errorResult = handleServiceError(error, "getExcelFinanciera");
    return createResponse(false, null, errorResult.message, {
      error: errorResult.message,
      details: errorResult.details,
      isCanceled: errorResult.isCanceled,
    });
  }
};

export default {
  getConciliations,
  getConciliationDetail,
  getConciliationKeys,
  uploadConciliationFiles,
  getConciliationDetailByKey,
  getConciliationDetailByIdAndConcession,
  getConciliationFilesById,
  getConciliationFilePreview,
  getNovelties,
  addNovelty,
  uploadExcelFinanciera,
  getExcelFinanciera,
  getPeajes,
};
